/**
 * Account API Service
 * Handles all API calls related to Chart of Accounts
 */

import apiService from './apiService';

export interface Account {
  id: string;
  code: string;
  name: string;
  accountType: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE';
  parentId?: string;
  currencyId?: string;
  reconcile: boolean;
  deprecated: boolean;
  note?: string;
  taxIds?: string[];
  companyId: string;
  createdAt: string;
  updatedAt: string;
  parent?: Account;
  children?: Account[];
}

export interface AccountBalance {
  account: Account;
  balance: {
    debit: string | number;
    credit: string | number;
    balance: string | number;
  };
}

export interface AccountFilters {
  accountType?: string;
  deprecated?: boolean;
  reconcile?: boolean;
  parentId?: string;
  search?: string;
}

export interface CreateAccountInput {
  code: string;
  name: string;
  accountType: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE';
  parentId?: string;
  currencyId?: string;
  reconcile?: boolean;
  note?: string;
  taxIds?: string[];
  companyId: string;
}

export interface UpdateAccountInput {
  code?: string;
  name?: string;
  accountType?: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE';
  parentId?: string;
  currencyId?: string;
  reconcile?: boolean;
  note?: string;
  taxIds?: string[];
}

class AccountService {
  private baseUrl = '/accounting/accounts';

  /**
   * Get all accounts with optional filters
   */
  async getAccounts(filters?: AccountFilters): Promise<Account[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.accountType) params.append('accountType', filters.accountType);
      if (filters?.deprecated !== undefined) params.append('deprecated', filters.deprecated.toString());
      if (filters?.reconcile !== undefined) params.append('reconcile', filters.reconcile.toString());
      if (filters?.parentId) params.append('parentId', filters.parentId);
      if (filters?.search) params.append('search', filters.search);

      const queryString = params.toString();
      const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

      const response = await apiService.get(url);
      return response.data || [];
    } catch (error: any) {
      console.error('Error fetching accounts:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch accounts');
    }
  }

  /**
   * Get account by ID
   */
  async getAccountById(id: string): Promise<Account> {
    try {
      const response = await apiService.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching account:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch account');
    }
  }

  /**
   * Get account with balance
   */
  async getAccountBalance(
    id: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<AccountBalance> {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const queryString = params.toString();
      const url = queryString
        ? `${this.baseUrl}/${id}/balance?${queryString}`
        : `${this.baseUrl}/${id}/balance`;

      const response = await apiService.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching account balance:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch account balance');
    }
  }

  /**
   * Get root accounts (accounts without parent)
   */
  async getRootAccounts(): Promise<Account[]> {
    try {
      const response = await apiService.get(`${this.baseUrl}/root`);
      return response.data || [];
    } catch (error: any) {
      console.error('Error fetching root accounts:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch root accounts');
    }
  }

  /**
   * Get accounts by type
   */
  async getAccountsByType(accountType: string): Promise<Account[]> {
    try {
      const response = await apiService.get(`${this.baseUrl}/type/${accountType}`);
      return response.data || [];
    } catch (error: any) {
      console.error('Error fetching accounts by type:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch accounts by type');
    }
  }

  /**
   * Create a new account
   */
  async createAccount(data: CreateAccountInput): Promise<Account> {
    try {
      const response = await apiService.post(this.baseUrl, data);
      return response.data;
    } catch (error: any) {
      console.error('Error creating account:', error);
      throw new Error(error.response?.data?.error || 'Failed to create account');
    }
  }

  /**
   * Update an account
   */
  async updateAccount(id: string, data: UpdateAccountInput): Promise<Account> {
    try {
      const response = await apiService.put(`${this.baseUrl}/${id}`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating account:', error);
      throw new Error(error.response?.data?.error || 'Failed to update account');
    }
  }

  /**
   * Deprecate (soft delete) an account
   */
  async deprecateAccount(id: string): Promise<Account> {
    try {
      const response = await apiService.patch(`${this.baseUrl}/${id}/deprecate`, {});
      return response.data;
    } catch (error: any) {
      console.error('Error deprecating account:', error);
      throw new Error(error.response?.data?.error || 'Failed to deprecate account');
    }
  }

  /**
   * Delete an account (hard delete)
   */
  async deleteAccount(id: string): Promise<void> {
    try {
      await apiService.delete(`${this.baseUrl}/${id}`);
    } catch (error: any) {
      console.error('Error deleting account:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete account');
    }
  }
}

export const accountService = new AccountService();
export default accountService;